/**
 * Validation utilities for file and configuration validation
 */

// Constants
export const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500 MB
export const LONG_CLIP_WARNING_THRESHOLD = 120; // 2 minutes
export const MIN_REPEAT_COUNT = 2;
export const MAX_REPEAT_COUNT = 10;
export const MIN_CROSSFADE_DURATION = 0.1;
export const MAX_CROSSFADE_DURATION = 3.0;

/**
 * Validation result type
 * @typedef {Object} ValidationResult
 * @property {boolean} valid - Whether validation passed
 * @property {string|null} error - Error message if invalid
 * @property {string|null} warning - Warning message (non-blocking)
 */

/**
 * Validate file type (preliminary MIME check)
 * @param {File} file - The file to validate
 * @returns {ValidationResult}
 */
export function validateFileType(file) {
  if (!file) {
    return { valid: false, error: 'No file selected', warning: null };
  }

  if (!file.type.startsWith('video/')) {
    return {
      valid: false,
      error: 'Please select a video file. Supported formats: MP4, WebM, MOV.',
      warning: null
    };
  }

  return { valid: true, error: null, warning: null };
}

/**
 * Validate file size
 * @param {File} file - The file to validate
 * @returns {ValidationResult}
 */
export function validateFileSize(file) {
  if (!file) {
    return { valid: false, error: 'No file selected', warning: null };
  }

  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(1);
    const maxMB = (MAX_FILE_SIZE / 1024 / 1024).toFixed(0);
    return {
      valid: false,
      error: `File is too large (${sizeMB} MB). Maximum size is ${maxMB} MB for browser processing.`,
      warning: null
    };
  }

  return { valid: true, error: null, warning: null };
}

/**
 * Validate video duration against crossfade duration
 * @param {number} videoDuration - Video duration in seconds
 * @param {number} crossfadeDuration - Crossfade duration in seconds
 * @returns {ValidationResult}
 */
export function validateDuration(videoDuration, crossfadeDuration) {
  if (!videoDuration || videoDuration <= 0) {
    return { valid: false, error: 'Could not determine video duration', warning: null };
  }

  if (videoDuration <= crossfadeDuration) {
    return {
      valid: false,
      error: `Video must be longer than crossfade duration (${crossfadeDuration}s). Your video is ${videoDuration.toFixed(1)}s.`,
      warning: null
    };
  }

  // Non-blocking warning for long clips
  let warning = null;
  if (videoDuration > LONG_CLIP_WARNING_THRESHOLD) {
    warning = `This is a long clip (${videoDuration.toFixed(0)}s). Processing may be slower on older hardware.`;
  }

  return { valid: true, error: null, warning };
}

/**
 * Validate repeat count
 * @param {number} repeatCount - The repeat count to validate
 * @returns {ValidationResult}
 */
export function validateRepeatCount(repeatCount) {
  if (!Number.isInteger(repeatCount)) {
    return { valid: false, error: 'Repeat count must be a whole number', warning: null };
  }

  if (repeatCount < MIN_REPEAT_COUNT || repeatCount > MAX_REPEAT_COUNT) {
    return {
      valid: false,
      error: `Repeat count must be between ${MIN_REPEAT_COUNT} and ${MAX_REPEAT_COUNT}`,
      warning: null
    };
  }

  return { valid: true, error: null, warning: null };
}

/**
 * Validate crossfade duration
 * @param {number} crossfadeDuration - The crossfade duration to validate
 * @returns {ValidationResult}
 */
export function validateCrossfadeDuration(crossfadeDuration) {
  if (typeof crossfadeDuration !== 'number' || isNaN(crossfadeDuration)) {
    return { valid: false, error: 'Crossfade duration must be a number', warning: null };
  }

  if (crossfadeDuration < MIN_CROSSFADE_DURATION || crossfadeDuration > MAX_CROSSFADE_DURATION) {
    return {
      valid: false,
      error: `Crossfade duration must be between ${MIN_CROSSFADE_DURATION}s and ${MAX_CROSSFADE_DURATION}s`,
      warning: null
    };
  }

  return { valid: true, error: null, warning: null };
}

/**
 * Validate crossfade duration against video duration
 * @param {number} crossfadeDuration - Crossfade duration in seconds
 * @param {number} videoDuration - Video duration in seconds
 * @returns {ValidationResult}
 */
export function validateCrossfadeVsDuration(crossfadeDuration, videoDuration) {
  if (!videoDuration) {
    return { valid: true, error: null, warning: null }; // Can't validate without video
  }

  if (crossfadeDuration >= videoDuration) {
    return {
      valid: false,
      error: `Crossfade duration (${crossfadeDuration}s) must be less than video duration (${videoDuration.toFixed(1)}s)`,
      warning: null
    };
  }

  return { valid: true, error: null, warning: null };
}

/**
 * Perform all file validations
 * @param {File} file - The file to validate
 * @returns {ValidationResult}
 */
export function validateFile(file) {
  const typeResult = validateFileType(file);
  if (!typeResult.valid) return typeResult;

  const sizeResult = validateFileSize(file);
  if (!sizeResult.valid) return sizeResult;

  return { valid: true, error: null, warning: null };
}

/**
 * Perform all configuration validations
 * @param {object} config - { repeatCount, crossfadeDuration }
 * @param {number|null} videoDuration - Video duration in seconds (optional)
 * @returns {ValidationResult}
 */
export function validateConfig(config, videoDuration = null) {
  const { repeatCount, crossfadeDuration } = config;

  const repeatResult = validateRepeatCount(repeatCount);
  if (!repeatResult.valid) return repeatResult;

  const crossfadeResult = validateCrossfadeDuration(crossfadeDuration);
  if (!crossfadeResult.valid) return crossfadeResult;

  if (videoDuration) {
    const durationResult = validateCrossfadeVsDuration(crossfadeDuration, videoDuration);
    if (!durationResult.valid) return durationResult;
  }

  return { valid: true, error: null, warning: null };
}

/**
 * Check if processing can proceed given current state
 * @param {object} state - Application state
 * @returns {ValidationResult}
 */
export function canProcess(state) {
  if (state.ffmpegStatus !== 'ready') {
    return { valid: false, error: 'Video engine not ready', warning: null };
  }

  if (!state.file) {
    return { valid: false, error: 'No video file selected', warning: null };
  }

  if (state.fileError) {
    return { valid: false, error: state.fileError, warning: null };
  }

  const configResult = validateConfig(state.config, state.file.duration);
  if (!configResult.valid) return configResult;

  const durationResult = validateDuration(state.file.duration, state.config.crossfadeDuration);
  if (!durationResult.valid) return durationResult;

  return { valid: true, error: null, warning: durationResult.warning };
}
